import { createAsyncThunk } from "@reduxjs/toolkit";

import { UserRole, type UserRoleEnum } from "@fitness-app/data-models";
import {
  MessageType,
  type ChatIncomingRealtimeEvents,
  type ChatMember,
  type ChatMessage,
  type ContextMessage,
  type MessageVisibleBy,
  type StandardMessage,
} from "@fitness-app/data-models/entities/Chat";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";
import { type Database } from "@fitness-app/supabase";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AsyncThunkCreator } from "../../../index";
import { addMessageToChannel, removeMessageFromChannel } from "../reducer";
import { CHAT_REDUCER_NAME } from "../types";

type Payload = {
  channelId: string;
  messageId?: string;
  selectedAuthor?: ChatMember | null;
  content: ChatMessage["content"];
  context?: ChatMessage["context"];
  files?: ChatMessage["files"];
  visibleBy?: MessageVisibleBy[];
  onRejected?: (error: string) => void;
  onSuccess?: () => void;
  isMobile?: boolean;
};

type ChatMessageInsert = Database["public"]["Tables"]["chat_message"]["Insert"];

const POSTGRES_UNIQUE_VIOLATION = "23505";

/**
 * Ids of messages whose insert is running right now. The optimistic copy says "sending" for as
 * long as it exists, so this is what tells a message genuinely on its way from one whose request
 * died with the app in the background (TRE-2263) - see `failStuckChatMessages`.
 */
const inFlightMessageIds = new Set<string>();

export const getInFlightChatMessageIds = (): string[] => [...inFlightMessageIds];

const trackInFlight = async <T>(messageId: string, request: PromiseLike<T>): Promise<T> => {
  inFlightMessageIds.add(messageId);
  try {
    return await request;
  } finally {
    inFlightMessageIds.delete(messageId);
  }
};


/** How long one attempt to save a message may take before it counts as failed (TRE-2263). */
export const SEND_MESSAGE_TIMEOUT_MS = 20_000;

export const sendNewMessage = createAsyncThunk<ChatMessage, Payload, AsyncThunkCreator<string>>(
  `${CHAT_REDUCER_NAME}/sendNewMessage`,
  async (
    payload,
    { rejectWithValue, dispatch, getState, extra: { db, auth, parties, analytics, errorTrackingService } },
  ) => {
    const currentUser = await getLoggedUser(auth);

    const message = {
      type: payload.context ? MessageType.WithContext : MessageType.Standard,
      content: payload.content,
      files: payload.files || [],
      context: payload.context || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      channelId: payload.channelId,
      authorRole:
        payload.selectedAuthor?.role ||
        (currentUser.customClaims?.role as UserRoleEnum | undefined) ||
        UserRole.TRAINER,
      authorId: payload.selectedAuthor?.uid || currentUser.id,
      automated: false,
      sendBy: currentUser.id,
      visibleBy: payload.visibleBy || ["all"],
      seenBy: [currentUser.id],
      deleted: false,
      id: payload.messageId || generateUniqId(),
      extra: {
        version: 2,
      },
    } as StandardMessage | ContextMessage;

    dispatch(
      addMessageToChannel({
        channelId: payload.channelId,
        message: {
          ...message,
          status: "sending",
        },
      }),
    );

    const socket = parties.chatRooms[payload.channelId];
    const socketNative = parties.chatRoomsNative[payload.channelId];

    const { createdAt, updatedAt, ...rest } = message;

    // A hung connection used to keep a message "sending" for up to 18 minutes: the transport waits
    // 2 minutes per request and retries it 3 times, and the send itself is retried on top. One
    // attempt now gives up after SEND_MESSAGE_TIMEOUT_MS; a write that did land is still picked up
    // by the next attempt as a unique violation, so cutting it short never duplicates the message.
    const attemptTimeout = new AbortController();
    const attemptTimer = setTimeout(() => attemptTimeout.abort(), SEND_MESSAGE_TIMEOUT_MS);
    const { error, data } = await trackInFlight(
      message.id,
      db
        .from("chat_message")
        .insert(rest as unknown as ChatMessageInsert)
        .select("*")
        .abortSignal(attemptTimeout.signal)
        .single<ChatMessage>(),
    ).finally(() => clearTimeout(attemptTimer));

    if (error && error.code !== POSTGRES_UNIQUE_VIOLATION) {
      if (payload.isMobile) {
        errorTrackingService?.log(`Cannot send message in action: ${error.message}`);
        payload.onRejected?.(error.message);
        // @ts-expect-error ignore
        error.messageId = message.id;
        return rejectWithValue(error.message);
      }

      const retries = getState().chat.sendRetriesCount;
      errorTrackingService?.recordError(error, "sendNewMessage", {
        postgresError: error.message,
        retries,
      });
      if (retries > 0) {
        dispatch(
          removeMessageFromChannel({
            channelId: payload.channelId,
            messageId: message.id,
          }),
        );
        void dispatch(sendNewMessage({ ...payload }));
        return rejectWithValue(error.message);
      }
      payload.onRejected?.(error.message);
      dispatch(
        removeMessageFromChannel({
          channelId: payload.channelId,
          messageId: message.id,
        }),
      );
      return rejectWithValue(error.message);
    }

    let deliveredMessage = data;

    if (!deliveredMessage) {
      // Unique violation on the primary key means a previous attempt (retried with the same id
      // after a lost response) already saved the message — treat it as delivered.
      errorTrackingService?.log(`Chat message ${message.id} already saved, treating duplicate key as delivered`);
      const { data: existing } = await db
        .from("chat_message")
        .select("*")
        .eq("id", message.id)
        .maybeSingle<ChatMessage>();
      deliveredMessage = existing ?? (message as ChatMessage);
    }

    payload.onSuccess?.();

    analytics.track("send_new_message", {
      channelId: payload.channelId,
      message: message.content?.length ?? 0,
      files: message.files?.length ?? 0,
    });

    if (socket) {
      const socketMessage: ChatIncomingRealtimeEvents = {
        type: "message",
        operation: "add",
        senderId: currentUser.id,
        data: message,
      };
      socket.send(JSON.stringify(socketMessage));
    }

    if (socketNative) {
      const socketMessage: ChatIncomingRealtimeEvents = {
        type: "message",
        operation: "add",
        senderId: currentUser.id,
        data: message,
      };
      socketNative.send(JSON.stringify(socketMessage));
    }

    return deliveredMessage;
  },
);
